import React from "react";
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { EditableTableRowProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import { Grid, Table, TableBody, TableCell, TableRow } from "@mui/material";
import Icon from "../../../icon";
import EditableTableCell from "./cell";
import EditableTableRowActions from "./rowActions";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const EditableTableRow: React.FC<EditableTableRowProps> = (props) => {
  const {
    row,
    rowIndex,
    dropRef,
    isDragging,
    handlerId,
    idFieldKey,
    disabled,
    onClick,
    isAddNewRow,
    allowReOrderingOfRows,
    nonTabbedFields,
    tabs,
    tabValue,
    errors,
    touched,
    isCompactTable,
    validationSchema,
    rowValues,
    tableReadonly,
    onMasterRowChange,
    getFieldName,
    getHasMasterRowEnabled,
    moreActions,
    hideActions,
    removeRowClickHandler,
    dragHoverState,
  } = props;

  return (
    <TableRow
      onClick={onClick}
      className={clsx(
        cssExports.fieldRow,
        isAddNewRow ? cssExports.tableLastRow : "",
        disabled ? cssExports.disabled : "",
        !isDragging && dragHoverState?.index === rowIndex
          ? dragHoverState!.dragAbove
            ? cssExports.dragAbove
            : cssExports.dragBelow
          : ""
      )}
      ref={isAddNewRow ? undefined : dropRef}
      style={isAddNewRow ? undefined : { opacity: isDragging ? 0.7 : 1 }}
      data-handler-id={isAddNewRow ? undefined : handlerId}
    >
      {allowReOrderingOfRows && (
        <TableCell className={isAddNewRow ? cssExports.tableLastRowNotDraggable : ""}>
          {isAddNewRow ? (
            <></>
          ) : (
            <div style={{ cursor: "move" }}>
              <Icon icon={IconDefinitionConstants.WListDrag} className={cssExports.listDragIcon} />
            </div>
          )}
        </TableCell>
      )}
      {nonTabbedFields.map(
        ([currentFieldName, currentField]) =>
          currentField && (
            <EditableTableCell
              key={`tab-cell-${row[idFieldKey] ?? rowIndex}-${currentFieldName}`}
              rowIndex={rowIndex}
              currentField={
                currentField.widthColumnCount ? { ...currentField, widthColumnCount: undefined } : currentField
              }
              cellValue={row[currentFieldName]}
              widthPercentage={
                currentField.widthColumnCount && typeof currentField.widthColumnCount === "number"
                  ? (currentField.widthColumnCount / 12) * 100
                  : undefined
              }
              fieldName={getFieldName(rowIndex, currentFieldName, true)}
              fieldNameForOptionalCheck={getFieldName(rowIndex, currentFieldName)}
              errors={errors && errors[currentFieldName]}
              touched={touched && touched[currentFieldName]}
              isCompactTable={isCompactTable}
              validationSchema={validationSchema}
              parentRowValues={rowValues}
              tableReadonly={tableReadonly}
              onMasterRowChange={onMasterRowChange}
            />
          )
      )}
      {tabs.length > 0 && (
        <TableCell className={cssExports.tabContainerCell}>
          {tabs.map(
            (tab, tabIndex) =>
              tabIndex === tabValue && (
                <Grid key={tabIndex} hidden={tabIndex !== undefined && tabIndex !== tabValue} item>
                  <Table>
                    <TableBody>
                      <TableRow>
                        {Object.entries(tab?.innerFields ?? {}).map(
                          ([currentFieldName, currentField], index, array) =>
                            currentField && (
                              <EditableTableCell
                                key={`tab-cell-${row[idFieldKey] ?? rowIndex}-${currentFieldName}`}
                                rowIndex={rowIndex}
                                tabIndex={tabIndex}
                                currentField={
                                  currentField.widthColumnCount
                                    ? { ...currentField, widthColumnCount: undefined }
                                    : currentField
                                }
                                cellValue={row[currentFieldName]}
                                widthPercentage={
                                  currentField.widthColumnCount && typeof currentField.widthColumnCount === "number"
                                    ? (currentField.widthColumnCount / 12) * 100
                                    : 100 / array.length
                                }
                                hasMasterRowEnabled={getHasMasterRowEnabled(tabIndex)}
                                fieldName={getFieldName(rowIndex, currentFieldName, true)}
                                fieldNameForOptionalCheck={getFieldName(rowIndex, currentFieldName)}
                                errors={props.errors && props.errors[currentFieldName]}
                                touched={props.touched && props.touched[currentFieldName]}
                                isCompactTable={isCompactTable}
                                validationSchema={validationSchema}
                                parentRowValues={rowValues}
                                onFocus={onClick}
                                tableReadonly={tableReadonly}
                                onMasterRowChange={onMasterRowChange}
                                allowReOrderingOfRows={allowReOrderingOfRows}
                              />
                            )
                        )}
                      </TableRow>
                    </TableBody>
                  </Table>
                </Grid>
              )
          )}
        </TableCell>
      )}
      {!disabled && !hideActions && !tableReadonly ? (
        <EditableTableRowActions
          rowKey={row[idFieldKey]}
          removeRowClickHandler={removeRowClickHandler}
          isLastRow={isAddNewRow}
          row={row}
          rowIndex={rowIndex}
          moreActions={moreActions}
        />
      ) : (
        <></>
      )}
    </TableRow>
  );
};

export default EditableTableRow;
